import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const AddReceivers = () => {
  const { accessToken } = useAuth();
  const location = useLocation();
  const { siteId } = location.state || {};
  const baseURL = process.env.REACT_APP_BASEURL;
  const mapRef = useRef(null);

  // State for receiver type
  const [receiverType, setReceiverType] = useState("echo");

  // State for form fields
  const [serialNumber, setSerialNumber] = useState("");
  const [baseId, setBaseId] = useState("");
  const [name, setName] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [height, setHeight] = useState("");
  const [antennaType, setAntennaType] = useState("Unknown");
  
  // State for sites
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  
  // State for map
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [isValidCoordinates, setIsValidCoordinates] = useState(true);
  const [mapEnabled, setMapEnabled] = useState(false);
  
  // State for loading and messages
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // State for Dialog
  const [receiverChoiceOpen, setReceiverChoiceOpen] = useState(true);

  // Antenna type options
  const antennaTypes = ["Unknown", "Directive Yogi", "Omni Pole", "Short Stub"];

  // Satellite tile layer
  const tileLayer = {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
  };

  // Fetch sites and user data, set default coordinates
  useEffect(() => {
    const fetchSitesAndUser = async () => {
      setLoadingSites(true);
      setError(null);
      try {
        // Fetch sites
        const siteRes = await axios.get(`${baseURL}/get-sites/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const siteList = siteRes.data.data || [];

        // Fetch user data
        const userRes = await axios.get(`${baseURL}/users/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userData = userRes.data[0] || {};
        const userSiteIds = userData.sites || [];

        // Filter user-assigned sites
        const userSites = siteList.filter((site) =>
          userSiteIds.includes(site.id)
        );
        setSites(userSites);

        // Set initial site
        const initialSite = userSites.some((site) => site.id === siteId)
          ? siteId
          : userSites.length > 0
          ? userSites[0].id
          : "";
        setSelectedSite(initialSite);

        // Fetch site details for coordinates
        if (initialSite) {
          const siteDetailsRes = await axios.get(
            `${baseURL}/sites/${initialSite}/?with_image=true`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
          const { latitude, longitude } = siteDetailsRes.data || {};
          if (latitude && longitude) {
            setLatitude(latitude.toString());
            setLongitude(longitude.toString());
            setMapCenter([latitude, longitude]);
          }
        }
      } catch (err) {
        setError("Failed to load sites. Please try again.");
        console.error("Sites fetch error:", err.response ? err.response.data : err.message);
      } finally {
        setLoadingSites(false);
      }
    };

    fetchSitesAndUser();
  }, [accessToken, baseURL, siteId]);

  // Update coordinates when site changes
  useEffect(() => {
    const fetchSiteCoordinates = async () => {
      if (!selectedSite) {
        setLatitude("");
        setLongitude("");
        setMapCenter([0, 0]);
        return;
      }
      try {
        const response = await axios.get(
          `${baseURL}/sites/${selectedSite}/?with_image=true`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        const { latitude, longitude } = response.data || {};
        if (latitude && longitude) {
          setLatitude(latitude.toString());
          setLongitude(longitude.toString());
          setMapCenter([latitude, longitude]);
        }
      } catch (err) {
        console.error("Site details fetch error:", err.response ? err.response.data : err.message);
      }
    };

    fetchSiteCoordinates();
  }, [accessToken, baseURL, selectedSite]);

  // Validate coordinates
  useEffect(() => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
    const isValidLon = !isNaN(lon) && lon >= -180 && lon <= 180;
    setIsValidCoordinates(isValidLat && isValidLon);
    if (isValidLat && isValidLon) {
      setMapCenter([lat, lon]);
    }
  }, [latitude, longitude]);

  // Map component to update coordinates on click
  const MapUpdater = ({ center, onClick }) => {
    const map = useMap();
    useEffect(() => {
      if (center[0] !== 0 && center[1] !== 0 && mapEnabled) {
        map.setView(center, 15);
      }
    }, [center, map, mapEnabled]);

    useEffect(() => {
      const handleClick = (e) => {
        const { lat, lng } = e.latlng;
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        onClick([lat, lng]);
      };
      map.on("click", handleClick);
      return () => map.off("click", handleClick);
    }, [map, onClick]);

    return null;
  };

  // Handle receiver type change
  const handleReceiverTypeChange = (event, newType) => {
    if (newType !== null) {
      setReceiverType(newType);
      // Reset form when switching types
      setSerialNumber("");
      setBaseId("");
      setName("");
      setHeight("");
      setAntennaType("Unknown");
      setError(null);
      setSuccess(null);
    }
  };

  // Handle site selection
  const handleSiteChange = (event) => {
    setSelectedSite(event.target.value);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoadingSubmit(true);

    // Validate inputs
    if (receiverType === "echo" && !serialNumber.trim()) {
      setError("Serial Number is required for Echo receivers.");
      setLoadingSubmit(false);
      return;
    }
    if (receiverType === "base" && !baseId.trim()) {
      setError("Base ID is required for Base receivers.");
      setLoadingSubmit(false);
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      setLoadingSubmit(false);
      return;
    }
    if (!latitude || !longitude || !isValidCoordinates) {
      setError("Valid latitude and longitude are required.");
      setLoadingSubmit(false);
      return;
    }
    if (!selectedSite) {
      setError("A site must be selected.");
      setLoadingSubmit(false);
      return;
    }

    try {
      const receiverData = {
        name,
        device_type: receiverType,
        location: {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          height: height ? parseFloat(height) : null,
        },
        antenna_type: antennaType,
        site: Number(selectedSite),
      };

      if (receiverType === "echo") {
        receiverData.serial_number = Number(serialNumber);
      } else {
        receiverData.base_id = baseId.trim();
      }

      await axios.post(`${baseURL}/devices/`, receiverData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      setSuccess(`${receiverType === "echo" ? "Echo" : "Base"} receiver added successfully!`);
      
      // Reset form
      setSerialNumber("");
      setBaseId("");
      setName("");
      setHeight("");
      setAntennaType("Unknown");
      setLatitude(sites.find((s) => s.id === selectedSite)?.latitude?.toString() || "");
      setLongitude(sites.find((s) => s.id === selectedSite)?.longitude?.toString() || "");
      setMapEnabled(false);
    } catch (err) {
      setError(`Failed to add ${receiverType} receiver. Please try again.`);
      console.error("Receiver creation error:", err.response ? err.response.data : err.message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Get display names
  const getSiteDisplayName = (site) => {
    return site.name || `Site ${site.id}`;
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}>
          Add New Receiver
        </Typography>
        <FormControl
          variant="outlined"
          sx={{ minWidth: { xs: "100%", sm: 200 }, width: { xs: "100%", sm: "auto" } }}
          disabled={loadingSites}
        >
          <InputLabel id="site-select-label">Site</InputLabel>
          <Select
            labelId="site-select-label"
            value={selectedSite}
            onChange={handleSiteChange}
            label="Site"
          >
            {loadingSites ? (
              <MenuItem value="" disabled>
                Loading sites...
              </MenuItem>
            ) : sites.length === 0 ? (
              <MenuItem value="" disabled>
                No sites available
              </MenuItem>
            ) : (
              sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {getSiteDisplayName(site)}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>

      {/* Receiver Type Toggle */}
      <Box sx={{ display: "flex", justifyContent: "start", mb: 3 }}>
        <ToggleButtonGroup
          color="primary"
          value={receiverType}
          exclusive
          onChange={handleReceiverTypeChange}
          aria-label="receiver type"
        >
          <ToggleButton value="echo">Echo</ToggleButton>
          <ToggleButton value="base">Base</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          
          {/* Receiver Identity and Name */}
          <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
            {receiverType === "echo" ? (
              <TextField
                label="Serial Number"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />
            ) : (
              <TextField
                label="Base ID"
                value={baseId}
                onChange={(e) => setBaseId(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />
            )}
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              variant="outlined"
            />
          </Box>

          {/* Location */}
          <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
            Location
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexDirection: { xs: "column", sm: "row" } }}>
            <TextField
              label="Latitude"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              fullWidth
              type="text"
              required
              error={!isValidCoordinates && latitude !== ""}
              helperText={
                !isValidCoordinates && latitude !== ""
                  ? "Latitude must be between -90 and 90"
                  : ""
              }
            />
            <TextField
              label="Longitude"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              fullWidth
              type="text"
              required
              error={!isValidCoordinates && longitude !== ""}
              helperText={
                !isValidCoordinates && longitude !== ""
                  ? "Longitude must be between -180 and 180"
                  : ""
              }
            />
          </Box>
          
          <Box sx={{ height: 300, border: "1px solid #ddd", mb: 2 }}>
            {mapEnabled && isValidCoordinates && latitude && longitude ? (
              <div ref={mapRef} style={{ height: "100%", width: "100%" }}>
                <MapContainer center={mapCenter} zoom={15} style={{ height: "100%", width: "100%" }}>
                  <TileLayer url={tileLayer.url} attribution={tileLayer.attribution} />
                  <TileLayer
                    url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    attribution=""
                  />
                  <Marker position={mapCenter} />
                  <MapUpdater center={mapCenter} onClick={setMapCenter} />
                </MapContainer>
              </div>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.100",
                  cursor: "pointer",
                }}
                onClick={() => setMapEnabled(true)}
              >
                <Typography color="text.secondary">
                  {latitude && longitude ? "Click the map to set coordinates" : "Click to enable map"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Height and Antenna Type */}
          <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
            <TextField
              label="Height (cm)"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: {
                    step: 1,
                    min: 0,
                  }
                }
              }}
              variant="outlined"
            />
            <FormControl fullWidth variant="outlined">
              <InputLabel id="antenna-type-select-label">Antenna Type</InputLabel>
              <Select
                labelId="antenna-type-select-label"
                value={antennaType}
                onChange={(e) => setAntennaType(e.target.value)}
                label="Antenna Type"
              >
                {antennaTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Save Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loadingSubmit || loadingSites || !selectedSite}
            sx={{ mt: 2, alignSelf: "flex-end" }}
          >
            {loadingSubmit ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </Box>
      </Paper>

      <Dialog open={receiverChoiceOpen}>
        <DialogTitle>Select Receiver Type</DialogTitle>

        <DialogContent>
          <Typography>Please choose the type of receiver you want to add:</Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setReceiverType("echo");
              setReceiverChoiceOpen(false);
            }}
          >
            Echo
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setReceiverType("base");
              setReceiverChoiceOpen(false);
            }}
          >
            Base
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddReceivers;
